import React, {useEffect, useRef, useState} from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {useIntl} from "react-intl";
import '../scss/TestSurvey.scss';
import {PostTestSurvey} from "../api/APISurvey";
import {useSnackbar} from "../Content";
import {SearchableDropdown, SearchDropdown, TagInput} from "../UIElements";
import {useDataContext} from "./SurveyContextProvider";
import {ReactComponent as LoadingIcon} from "../svg/loading.svg";
import {ReactComponent as Alert} from "../svg/alert.svg";
import {changeSupportTypesToProvider, reconstructConfigObject, removeUnnecessaryKeys} from "../DataFunctions";
import {useCurrentUserData, useLanguageData} from "../App";
import {PostAssistant, PostAssistantWithId, GetAssistant} from "../api/APIBot";
import {useAssistantOperations} from "../utils/assistantUtils";
import {getInstanceConfigValue} from "../utils";

export const TestSurveyModal = ({ onClose }) => {
    const intl = useIntl();
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData');
    const { value: userData } = useDataContext('userData');
    const { value: instanceConfig } = useDataContext('instanceConfig');
    const { value: contactMetaArray } = useDataContext('contactMetaArray');
    const { currentUserData } = useCurrentUserData();
    const { setSnackbarData } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const postTestSurvey = PostTestSurvey();

    // Detect if using WhatsApp channel
    const channelValue = getInstanceConfigValue("contact", "channel", instanceConfig);
    const isWhatsAppInstance = Array.isArray(channelValue)
        ? channelValue.some(channel => channel.toLowerCase().includes("whatsapp"))
        : channelValue?.toLowerCase()?.includes("whatsapp");

    const testAssistantId = surveyData?.config?.testAssistantid || "";
    const postAssistantWithId = PostAssistantWithId(testAssistantId);
    const postAssistant = PostAssistant();
    const getAssistant = GetAssistant(testAssistantId, surveyData?.config?.type === "SUPPORTBOT");
    const newID = useRef(new Date().getTime().toString());

    const { buildAssistantInstructions, fetchCombinedToolsAndResources } = useAssistantOperations();

    // Message constants
    const testSurveyHeader = intl.formatMessage({defaultMessage:'Test Your Survey',id:'testSurveyHeader'});
    const testSurveyDescription = intl.formatMessage({defaultMessage:'Preview and refine your survey by sending a test survey by SMS. Any support requests generated from the test will be automatically deleted, and test data won´t affect your survey statistics.',id:'testSurveyDescription'});
    const testRecipientsText = intl.formatMessage({defaultMessage:'Test Recipients',id:'testSurveyRecipientsText'});
    const recipientsPlaceholder = intl.formatMessage({defaultMessage:'Provide phonenumbers with country code eg. +358401234567',id:'surveyRecipientsInputPlaceholder'});
    const variablesHeader = intl.formatMessage({defaultMessage:'Customize variables found in survey',id:'customizeVariablesHeader'});
    const variablePlaceholder = intl.formatMessage({defaultMessage:'Type name to use for variable in test survey',id:'variableInputPlaceholder'});
    const sendTestText = intl.formatMessage({defaultMessage:'Send Test', id:'sendTest'});
    const sendingTestText = intl.formatMessage({defaultMessage:'Sending Test', id:'sendingTestText'});
    const postSuccessMessage = intl.formatMessage({defaultMessage:'Test Survey Sent!', id:'postTestSurveySuccess'});
    const postFailMessage = intl.formatMessage({defaultMessage:'Sending test survey failed. Please try again later.', id:'postTestSurveyFail'});
    const targetSupportNeedsHeader = intl.formatMessage({defaultMessage:'Assign support requests from test', id:'assignTestSupportRequests'});
    const missingInitialMessageText = intl.formatMessage({defaultMessage:'No message to send. Please add text to the first message field.', id:'testSurvey.messageTextMissingError'});

    const missingInitialMessage = surveyData?.config?.message?.length < 1;

    const findVariablesInMessages = config => {
        const regex = /{{(.*?)}}/g
        let match
        const foundVariables = new Set()
        const variables = []

        function searchMessages(obj) {
            Object.entries(obj).forEach(([key, value]) => {
                if (typeof value === 'object' && value !== null) {
                    searchMessages(value)
                } else if (key === 'message' && typeof value === 'string') {
                    while ((match = regex.exec(value)) !== null) {
                        if (!foundVariables.has(match[1])) {
                            foundVariables.add(match[1])
                            variables.push({ variable: match[1], value: match[1], message: value })
                        }
                    }
                }
            })
        }

        searchMessages(config)
        return variables
    }

    const findSupportNeeds = config => {
        return Object.keys(config).some(key => {
            const branch = config[key]
            return branch && typeof branch === 'object' && branch?.supportneed === true
        })
    }

    const findUsersWithPhonenumber = data => {
        const users = []
        data.forEach(obj => {
            if (obj.hasOwnProperty("meta") && obj.meta.hasOwnProperty("phonenumber")) {
                const phonenumber = obj.meta.phonenumber
                if (typeof phonenumber === 'string' && phonenumber !== '') {
                    const userMeta = obj.meta
                    users.push(userMeta)
                }
            }
        })
        return users
    }
    const users = findUsersWithPhonenumber(userData)

    const userDataWithMeta = data => {
        const users = []
        data.forEach(obj => {
            if (obj.hasOwnProperty("meta")) {
                const userMeta = obj.meta
                userMeta["id"] = obj.id
                users.push(userMeta)
            }
        })
        return users
    }
    const usersWithMeta = userDataWithMeta(userData)

    const variableNames = findVariablesInMessages(surveyData?.config)

    // For WhatsApp instances, filter out WhatsApp variables (they get content from survey config)
    const filteredVariables = isWhatsAppInstance
        ? variableNames.filter(varObj =>
            contactMetaArray?.includes(varObj.variable) ||
            !surveyData?.config?.whatsappVariables?.hasOwnProperty(varObj.variable)
        )
        : variableNames;

    const [variables, setVariables] = useState(filteredVariables)
    const [phoneNumbers, setPhoneNumbers] = useState('')
    const [supportNeedTarget, setSupportNeedTarget] = useState(currentUserData?.id || '')

    const surveyHasVariables = variables?.length > 0
    const surveyHasSupportNeeds = findSupportNeeds(surveyData?.config)

    const handleAssistantOperations = async (callback) => {
        if (surveyData?.config?.type === "SUPPORTBOT") {
            // Build test assistant instructions
            const surveyId = surveyData?.id === "new" ? newID.current : surveyData?.id;
            const processedAssistantData = buildAssistantInstructions(surveyData, surveyId, true);

            // Always fetch tools/resources from support bots for test independence
            try {
                if (surveyData?.config?.supportBots) {
                    const { tools, tool_resources } =
                        await fetchCombinedToolsAndResources(surveyData?.config?.supportBots);

                    if (tools) {
                        processedAssistantData.tools = tools;
                    }
                    if (tool_resources) {
                        processedAssistantData.tool_resources = tool_resources;
                    }
                }
            } catch (error) {
                console.error('Error fetching assistant tools for test:', error);
            }

            if (testAssistantId) {
                if (getAssistant.isLoading) {
                    return;
                } else if (getAssistant.isSuccess && getAssistant.data) {
                    // Update existing test assistant
                    postAssistantWithId.mutate(processedAssistantData, {
                        onError: () => {
                            setSnackbarData({
                                show: true,
                                text: postFailMessage,
                                state: "error",
                                autoHide: true,
                            });
                            setLoading(false);
                        },
                        onSuccess: () => {
                            callback(testAssistantId);
                        }
                    });
                } else if (getAssistant.isError) {
                    setSnackbarData({
                        show: true,
                        text: postFailMessage,
                        state: "error",
                        autoHide: true,
                    });
                    setLoading(false);
                }
            } else {
                // Create new test assistant
                postAssistant.mutate(processedAssistantData, {
                    onError: () => {
                        setSnackbarData({
                            show: true,
                            text: postFailMessage,
                            state: "error",
                            autoHide: true,
                        });
                        setLoading(false);
                    },
                    onSuccess: (response) => {
                        if (response && response?.data?.id) {
                            const updatedSurveyData = {
                                ...surveyData,
                                config: {
                                    ...surveyData.config,
                                    testAssistantid: response.data.id
                                }
                            };
                            setSurveyData(updatedSurveyData);
                            callback(response.data.id);
                        } else {
                            callback(null);
                        }
                    }
                });
            }
        } else {
            // Non-SUPPORTBOT surveys proceed without assistant
            callback(null);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setLoading(true);

        // Process assistant first, then continue with test
        handleAssistantOperations((testAssistantid) => {
            const newSurveyData = {...surveyData};
            const cleanedSurveyData = removeUnnecessaryKeys(newSurveyData);
            const surveySupportNeedsToSupportProvider = changeSupportTypesToProvider(cleanedSurveyData);

            const categoriesForSupportNeedTargeting = Object.keys(surveySupportNeedsToSupportProvider?.config).reduce((acc, key) => {
                const data = cleanedSurveyData?.config;
                const branch = data[key];
                if (branch && typeof branch === 'object' && branch?.supportneed) {
                    acc[branch?.category || 'Z'] = true;
                }
                return acc;
            }, {});

            const surveyForTest = reconstructConfigObject(surveySupportNeedsToSupportProvider);

            // Use test assistant for the test survey
            if (testAssistantid && surveyForTest?.config) {
                surveyForTest.config.assistantid = testAssistantid;
            }

            const surveyConfigForTest = surveyForTest?.config;

            const annieUserArray = supportNeedTarget ? supportNeedTarget.split(',').map(userId => ({
                annieuser: userId,
                meta: {
                    category: categoriesForSupportNeedTargeting
                }
            })) : [];

            const createContactMetas = () => {
                return phoneNumbers.split(',').map(number => {
                    const contactMeta = { phonenumber: number.trim() };

                    // Add user-editable variables
                    variables.forEach(variable => {
                        const { variable: varName, value: defaultValue } = variable;
                        contactMeta[varName] = defaultValue;
                    });

                    // For WhatsApp instances, add WhatsApp variables from survey config
                    if (isWhatsAppInstance && surveyData?.config?.whatsappVariables) {
                        Object.entries(surveyData.config.whatsappVariables).forEach(([varName, value]) => {
                            contactMeta[varName] = value;
                        });
                    }

                    return contactMeta;
                });
            };

            const data = {
                contactmetas: createContactMetas(),
                surveyconfig: surveyConfigForTest,
                annieusersurveys: annieUserArray
            };

            postTestSurvey.mutate(data, {
                onError: () => {
                    setSnackbarData({
                        show: true,
                        text: postFailMessage,
                        state: "error",
                        autoHide: true,
                    });
                    setLoading(false);
                },
                onSuccess: () => {
                    setSnackbarData({
                        show: true,
                        text: postSuccessMessage,
                        state: "success",
                        autoHide: true,
                    });
                    setLoading(false);
                    onClose();
                }
            });
        });
    };

    const handleVariableChange = (variable, value) => {
        setVariables((prevVariables) =>
            prevVariables.map(v =>
                v.variable === variable ? { ...v, value: value } : v
            )
        );
    };

    const findSentenceWithVariable = (message, variable) => {
        const sentences = message.match(/[^\.!\?]+[\.!\?]+/g) || [];
        return sentences.find(sentence => sentence.includes(`{{${variable}}}`)) || '';
    };

    const isItAphoneNumberRegex = /^\+\d{1,4}\d{3,11}$/;

    const renderUserLabel = userId => {
        const user = usersWithMeta.find(obj => obj?.id === userId);
        return `${user?.firstname} ${user?.lastname}${user?.email ? ` – ${user?.email}`:''}`;
    };

    const isProcessing = loading || postAssistantWithId.isLoading || postAssistant.isLoading || getAssistant.isLoading;

    return <div className={"test-survey"}>
        <h1>{testSurveyHeader}</h1>
        <p>{testSurveyDescription}</p>
        <hr/>
        <h2>
            {testRecipientsText}
        </h2>
        <div>
            <TagInput
                onChange={(value)=>setPhoneNumbers(value)}
                duplicateAlert={intl.formatMessage({defaultMessage:'This phonenumber has already been given',id:'duplicateNumberAlert'})}
                placeholder={recipientsPlaceholder}
                value={phoneNumbers}
                testRegex={isItAphoneNumberRegex}
                regexAlert={intl.formatMessage({defaultMessage:'Invalid phone number format. Please use the format: +[country code][number]. For example, +3584012345678.', id:'invalidPhonenumberFormatAlert'})}
                searchContent={users}
                searchKeys={["phonenumber","firstname","lastname"]}
            />
            {surveyHasVariables &&
                <>
                    <h2>{variablesHeader}</h2>
                    <div className={"table"}>
                        {variables.map((obj) => {
                            const variable = obj?.variable || ''
                            const value = obj?.value || ''
                            const message = obj?.message || ''
                            const sentenceWithVariable = findSentenceWithVariable(message, variable)
                            const sentenceParts = sentenceWithVariable.split(`{{${variable}}}`);
                            const beforeVariable = sentenceParts[0];
                            const afterVariable = sentenceParts[1] || ''

                            return  <div className={"table-row"} key={variable}>
                                        <span className={"variable"}>
                                            {intl.formatMessage({defaultMessage:variable, id:`contactmeta.${variable}`})}
                                        </span>
                                <input
                                    type="text"
                                    placeholder={variablePlaceholder}
                                    value={value || ''}
                                    onChange={(e) => handleVariableChange(variable, e.target.value)}
                                />
                                <p><em>{beforeVariable}<b>{value}</b>{afterVariable}</em></p>
                            </div>
                        })}
                    </div>
                </>
            }
            {surveyHasSupportNeeds &&
                <>
                    <h2>{targetSupportNeedsHeader}</h2>
                    <TagInput
                        onChange={(value) => setSupportNeedTarget(value)}
                        placeholder={intl.formatMessage({defaultMessage:'Assign support requests by selecting a user', id:'assignTestRequestsPlaceholder'})}
                        value={supportNeedTarget}
                        inputIsSearch={true}
                        searchContent={usersWithMeta}
                        idKey={'id'}
                        searchKeys={["firstname", "lastname", "email"]}
                        tagDisplay={tag => renderUserLabel(tag)}
                    />
                </>
            }
            <hr/>
            <div className={"modal-options"}>
                {missingInitialMessage &&
                    <p className={"alert"}>
                        <Alert />
                        {missingInitialMessageText}
                    </p>
                }
                <button type="button" className={"cancel"} onClick={onClose}>{intl.formatMessage({defaultMessage:'Cancel',id:'modal.cancel'})}</button>
                {isProcessing ?
                    <button disabled className={"confirm"}><LoadingIcon />{sendingTestText}</button> :
                    (phoneNumbers?.length > 0 && !missingInitialMessage) ?
                        <button onClick={handleSubmit} className={"confirm"}>{sendTestText}</button> :
                        <button disabled className={"confirm"}>{sendTestText}</button>
                }
            </div>
        </div>
    </div>
};